import type { VlanMode } from '@simpleacs/shared';

/**
 * The WAN dialog as a stepped wizard — which steps exist, and where each one
 * OPENS. Pure, so the decisions are pinned without a DOM.
 *
 * Operator, 2026-09-13, with screenshots of the one-screen dialog: "the steps
 * should be like the mngt mode modal", "looking like moving between tabs";
 * "leave as is should not be an option, also selectable options, the default
 * should be what is already selected if it exists"; "split ipv6 and ipv6 into
 * 2 rows". So every choice is a real option pre-selected to the current value
 * where one is known, and the "Leave as it is" firewall option and the "Leave
 * as the CPE has it" IPv6 tile are gone. What replaces them is not a hidden
 * default: each step opens on the value the row / the CPE / the platform
 * already holds, and the Review step lists exactly what is sent.
 *
 * The DEFAULTS — `defaultAddressing`, `addressingReady`, `addressingPatch`,
 * the IPv6 choice matrix and `defaultIpv6Intent` / `defaultIpv6Modes`,
 * `defaultFirewall`, `firewallModelNote` — moved to
 * `packages/shared/src/wan-wizard.ts` on 2026-09-14 when the bulk `wan_mode`
 * action arrived: "opens on the current value" is a rule about one ONU, and
 * the bulk resolver has to answer it per ONU with exactly the dialog's order.
 * They are re-exported here so the dialog and `__tests__/wan-wizard.test.ts`
 * import unchanged; only the step list below is UI and stays web-side.
 */
export {
  IPV6_ADDRESS_DEFAULT_ORDER,
  IPV6_ADDRESS_MODES,
  IPV6_PREFIX_DEFAULT_ORDER,
  IPV6_PREFIX_MODES,
  addressingPatch,
  addressingReady,
  defaultAddressing,
  defaultFirewall,
  defaultIpv6Intent,
  defaultIpv6Modes,
  firewallModelNote,
  ipv6ChoiceMatrix,
  type Ipv6ChoiceMatrix,
} from '@simpleacs/shared';

export type WanWizardStepKey = 'vlan' | 'mode' | 'addressing' | 'ipv6' | 'firewall' | 'review';

export interface WanWizardStep {
  key: WanWizardStepKey;
  title: string;
}

/** The pill-row words — short, because six of them share the header line. */
export const WAN_WIZARD_STEP_TITLES: Record<WanWizardStepKey, string> = {
  vlan: 'VLAN',
  mode: 'Mode',
  addressing: 'Addressing',
  ipv6: 'IP protocol',
  firewall: 'Firewall',
  review: 'Review',
};

/**
 * The steps, in order, for the choice being built.
 *
 *   VLAN → Mode → [Addressing → IP protocol] → [Firewall] → Review
 *
 * Addressing and IP protocol are ROUTING questions — a bridged VLAN is handed
 * to the subscriber's own router, which does its own DHCP/PPPoE and its own
 * IPv6 — so they leave the row when Bridging is chosen, rather than sitting
 * greyed. A mode not yet chosen (an ONU with no subscriber VLAN yet) shows
 * the routed row: the operator cannot pass Mode without choosing, and the
 * longer row is the one that shrinks, never one that grows under them.
 *
 * Firewall is a ZTE verb (the /omci-wan and /firewall routes refuse a Huawei
 * chassis), so on a Huawei the step is absent — not shown with nothing to
 * choose. IP protocol likewise leaves the row when the target refuses EVERY
 * intent (an OMCI-managed ONU, a Tenda's numeric mode, a Nokia with no leaf):
 * with "leave as the CPE has it" gone there would be two greyed tiles and no
 * way past Next, and the dialog's other writes — the VLAN, the mode, the
 * firewall — must stay reachable on those units (additive rule). The Review
 * step then says in the gate's own words why IPv6 is not written.
 */
export function wanWizardSteps(input: {
  mode: VlanMode | null;
  firewallApplicable: boolean;
  /** False when the target refuses every intent; the step is then absent. Default true. */
  ipv6Applicable?: boolean;
}): WanWizardStep[] {
  const routed = input.mode !== 'bridge';
  const keys: WanWizardStepKey[] = [
    'vlan',
    'mode',
    ...(routed ? (['addressing'] as const) : []),
    ...(routed && input.ipv6Applicable !== false ? (['ipv6'] as const) : []),
    // A bridge has no firewall of its own to set — the router behind it does
    // the firewalling (operator, 2026-10-14: "a bridging onu should not have
    // addressing mode or firewall options").
    ...(routed && input.firewallApplicable ? (['firewall'] as const) : []),
    'review',
  ];
  return keys.map((key) => ({ key, title: WAN_WIZARD_STEP_TITLES[key] }));
}
